import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { tap, filter, take, map, switchMap, catchError } from 'rxjs/operators';
import { StoreService } from './store.service';
import { DataService } from './data.service';

export interface AiCreditState {
  /** Whether the AI button can be used. */
  enabled: boolean;
  /** Credits one generation costs, from the backend config; null when credits aren't charged or the price can't be loaded. */
  tokenCost: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class DealerService {
  private dealerSubject = new BehaviorSubject<any>(null);
  // public dealerInfo$: Observable<any | null>;
  dealerData;
  dealer$ = this.dealerSubject.asObservable();

  constructor(private svc: StoreService, private dataService: DataService) {
    // this.fetchDealerInfo();
  }

  async fetchDealerInfo(): Promise<any> {
    const token = localStorage?.getItem('bearer');

    try {
      const res: any = await this.svc.getDealerInfo(token).toPromise();
      this.dealerData = res?.data;
      this.dealerSubject.next(this.dealerData);
      return this.dealerData;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  getDealerInfo() {
    if (this.dealerSubject.value === null) {
      this.fetchDealerInfo();
    }
    return this.dealer$;
  }

  getDealer(): Observable<any> {
    if (!this.dealerData) {
      this.fetchDealerInfo();
    }

    return this.dealer$.pipe(
      filter((data) => data !== null),
      take(1)
    );
  }

  updateDealerInfo(newDealerInfo: Partial<any>): void {
    const currentDealer = this.dealerSubject.value;
    if (currentDealer) {
      const updatedDealer = { ...currentDealer, ...newDealerInfo };
      this.dealerSubject.next(updatedDealer);
      console.log('Updated dealer info:', this.dealerSubject.value);
    } else {
      this.dealerSubject.next(newDealerInfo);
      console.log('Set new dealer info:', newDealerInfo);
    }
  }

  /**
   * Resolves the DEALER_SUBSCRIPTION flag (true = AI credits are charged) from dealer info,
   * fetching it if it hasn't been loaded yet. Emits null if dealer info can't be loaded.
   */
  getSubscriptionStatus(): Observable<boolean | null> {
    const toStatus = (dealer: any): boolean | null => {
      const status = dealer?.dealer_plan?.dealer_subscription_status;
      return typeof status === 'boolean' ? status : null;
    };
    const loaded = this.dealerSubject.value || this.dealerData;
    const dealer$ = loaded ? of(loaded) : from(this.fetchDealerInfo());

    return dealer$.pipe(
      map(toStatus),
      tap((status) => {
        if (status !== null) this.dataService.updateDealerSubStatus(status);
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Price and access for an AI feature billed under `configKey` (e.g. AI_REPORT_SUMMARY_CREDIT).
   * Waits for the real subscription status: DataService defaults to false before dealer info loads,
   * which would otherwise hide the price on pages opened directly.
   */
  getAiCreditState(configKey: string): Observable<AiCreditState> {
    const notCharged: AiCreditState = { enabled: true, tokenCost: null };
    const dealerId = this.svc.userInfo?.d_id;
    const token = localStorage.getItem('bearer');

    return this.getSubscriptionStatus().pipe(
      switchMap((subscriptionOn): Observable<AiCreditState> => {
        if (subscriptionOn === false || !dealerId || !token) return of(notCharged);

        return this.svc.getDealerCredit(token, dealerId, configKey).pipe(
          map((res: any): AiCreditState => {
            const credits = res?.data;
            this.dataService.updateDealerCredits(credits);
            const tokenCost = Number(credits?.minimumCredit) || null;
            return {
              enabled: credits?.is_valid === true && credits?.totalCredit >= (tokenCost ?? 0),
              tokenCost,
            };
          }),
          // The backend still enforces credits on generate, so don't block the button on a lookup failure
          catchError(() => of(notCharged))
        );
      })
    );
  }
}
